import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stepUpHeaders } from "../../gateway/api";
import { AFFILIATE_PATHS } from "../../affiliate/contract";
import { useLanguage, useT } from "../../lib/LanguageContext";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";
import StepUpConfirmDialog from "../../gateway/components/StepUpConfirmDialog";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { apiErrorCode } from "../../lib/apiErrors";
import { fmtDate } from "../../admin/affiliate/format";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useScreenWrites } from "../screenWrites";

const MIN_WINDOW_DAYS = 1;
const MAX_WINDOW_DAYS = 365;
const MIN_SAAS_MONTHS = 0;
const MAX_SAAS_MONTHS = 60;

/**
 * ★ `PUT /super-admin/affiliate/program` — the three numbers the whole module reads.
 *
 * A FULL REPLACEMENT, NOT A PATCH, because every accrual reads the three together and a
 * half-stated policy is not a policy. The bounds mirror the pydantic ones exactly
 * (`1..365`, `0..60`); a form laxer than the API only moves the rejection to an opaque 422,
 * and `saasWindowMonths: 0` is LEGAL — it means "the first paid invoice only".
 *
 * CHANGING A WINDOW NEVER RESTATES MONEY ALREADY BOOKED: `windowEndsAt` is snapshotted on
 * the referral at activation. The attribution window, by contrast, is judged by the server at
 * each sale against the value in force then, so a change applies to every visit not yet
 * converted. The form says both.
 */
export default function ProgramPolicyForm({ gatewayApi }) {
  // Every affiliate write is finance.write behind a step-up (minted with finance.write too).
  const { manage: canManage } = useScreenWrites("affiliate");
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [program, setProgram] = useState(null);
  const [form, setForm] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(AFFILIATE_PATHS.adminProgram);
    if (!data || typeof data !== "object" || typeof data.enabled !== "boolean"
        || !Number.isInteger(data.attributionWindowDays)
        || !Number.isInteger(data.saasWindowMonths)) {
      throw payloadShapeError();
    }
    setProgram(data);
    setForm({
      enabled: data.enabled,
      attributionWindowDays: String(data.attributionWindowDays),
      saasWindowMonths: String(data.saasWindowMonths),
    });
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  const inRange = (value, min, max) => {
    const n = Number(value);
    return Number.isInteger(n) && n >= min && n <= max;
  };
  const valid = !!form
    && inRange(form.attributionWindowDays, MIN_WINDOW_DAYS, MAX_WINDOW_DAYS)
    && inRange(form.saasWindowMonths, MIN_SAAS_MONTHS, MAX_SAAS_MONTHS);

  const confirm = async ({ stepUpToken }) => {
    setRefusal("");
    try {
      const { data } = await api.put(AFFILIATE_PATHS.adminProgram, {
        enabled: form.enabled,
        attributionWindowDays: Number(form.attributionWindowDays),
        saasWindowMonths: Number(form.saasWindowMonths),
      }, stepUpHeaders(stepUpToken));
      setProgram(data);
      toast.success(t("saff.policy.saved"));
      return { ok: true };
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "REAUTH_REQUIRED") return { ok: false, code };
      setRefusal(affiliateErrorText(e, { t, tv, prefix: "saff", fallback: tv("errors.saveFailed") }));
      return { ok: false, code: null };
    }
  };

  if (loadError) {
    return (
      <LoadFailure
        message={affiliateErrorText(loadError, { t, tv, prefix: "saff", fallback: tv("errors.loadFailed") })}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="sa-affiliate-policy-load-error"
        retryTestId="sa-affiliate-policy-retry"
      />
    );
  }
  if (!form) return <p className="text-slate-400 text-sm">{t("saff.loading")}</p>;

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="sa-affiliate-policy">
      <h2 className="text-xl text-slate-800">{t("saff.policy.title")}</h2>
      <p className="text-xs text-slate-400 mt-1">{t("saff.policy.desc")}</p>

      <label className="flex items-center gap-2 text-sm mt-4">
        <input
          type="checkbox"
          checked={form.enabled}
          disabled={!canManage}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          data-testid="sa-affiliate-enabled"
        />
        {t("saff.policy.enabled")}
      </label>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div>
          <Label htmlFor="sa-affiliate-window-days">
            {t("saff.policy.windowDays")}
          </Label>
          <input
            id="sa-affiliate-window-days"
            type="number"
            min={MIN_WINDOW_DAYS}
            max={MAX_WINDOW_DAYS}
            value={form.attributionWindowDays}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, attributionWindowDays: e.target.value })}
            data-testid="sa-affiliate-window-days"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            {t("saff.policy.windowDaysHint", { min: MIN_WINDOW_DAYS, max: MAX_WINDOW_DAYS })}
          </p>
        </div>
        <div>
          <Label htmlFor="sa-affiliate-saas-months">
            {t("saff.policy.saasMonths")}
          </Label>
          <input
            id="sa-affiliate-saas-months"
            type="number"
            min={MIN_SAAS_MONTHS}
            max={MAX_SAAS_MONTHS}
            value={form.saasWindowMonths}
            disabled={!canManage}
            onChange={(e) => setForm({ ...form, saasWindowMonths: e.target.value })}
            data-testid="sa-affiliate-saas-months"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            {t("saff.policy.saasMonthsHint", { min: MIN_SAAS_MONTHS, max: MAX_SAAS_MONTHS })}
          </p>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-4">{t("saff.policy.notRetroactive")}</p>
      {program?.updatedAt && (
        <p className="text-[11px] text-slate-400 mt-1" data-testid="sa-affiliate-policy-stamp">
          {/* A program document with no recorded author (legacy, or set in the database)
              drops the "by" clause rather than naming an empty one. */}
          {typeof program.updatedBy === "string" && program.updatedBy.trim()
            ? t("saff.policy.updatedBy", { who: program.updatedBy.trim(), when: fmtDate(program.updatedAt, lang) })
            : t("saff.policy.updated", { when: fmtDate(program.updatedAt, lang) })}
        </p>
      )}
      {refusal && (
        <p className="text-xs text-rose-600 mt-2" role="alert" data-testid="sa-affiliate-policy-error">{refusal}</p>
      )}

      {canManage && <>
      <Button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={!valid}
        data-testid="sa-affiliate-policy-save"
        // Same measured-contrast override as AffiliateRulesEditor.jsx's save button: the
        // shared Button's default variant fails WCAG AA here (2.22:1), the gold CTA is
        // 10.26:1 and matches the other 19 super-admin screens.
        className="mt-4 bg-sol-gold text-sol-midnight hover:bg-sol-gold/90"
      >
        {t("saff.policy.save")}
      </Button>

      <StepUpConfirmDialog
        open={confirming}
        onClose={(didMutate) => { setConfirming(false); setRefusal(""); if (didMutate) reload(); }}
        titleKey="saff.policy.confirmTitle"
        descKey="saff.policy.confirmDesc"
        requireAck
        ackKey="saff.policy.ack"
        onConfirm={confirm}
        gatewayApi={gatewayApi}
      />
      </>}
    </section>
  );
}
